import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useFieldglassJobPostingQuery } from 'queries/admin/fieldglass/useFieldglassJobPostingQuery'
import { useFieldglassJobPostingFiltersQueryState } from '../hooks/useFieldglassJobPostingFiltersQueryState'

type FieldglassJobPostingContextState = {
  state: {
    data: ReturnType<typeof useFieldglassJobPostingQuery>['data']
    jobPostingId: string | null
    dataUpdatedAt: number
    isPending: boolean
    isFetching: boolean
    isError: boolean
    error: string
    hasSearchParams: boolean
  }
  actions: {
    setJobPostingId: (jobPostingId: string | null) => void
    refetch: () => void
  }
}

const [Context, useFieldglassJobPostingContextValue] =
  createContext<FieldglassJobPostingContextState>({
    name: 'FieldglassJobPostingContext',
  })

export const FieldglassJobPostingProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useFieldglassJobPostingFiltersQueryState()

  const jobPostingId = filters.jobPostingId
  const hasSearchParams = jobPostingId !== null && jobPostingId !== ''
  const request = hasSearchParams ? { jobPostingId } : null

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useFieldglassJobPostingQuery(request)

  const value: FieldglassJobPostingContextState = {
    state: {
      data,
      jobPostingId,
      dataUpdatedAt,
      isPending: hasSearchParams && isPending,
      isFetching,
      isError: hasSearchParams && isError,
      error: parseErrorMessage(error),
      hasSearchParams,
    },
    actions: {
      setJobPostingId: (nextJobPostingId) => {
        void setFilters({ jobPostingId: nextJobPostingId })
      },
      refetch: () => {
        void refetch()
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useFieldglassJobPostingContextValue }
